import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Drawer, IconButton, SxProps, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { BOTTOM_SHEET_FIXED_HEIGHT_PERCENT, DRAWER_Z_INDEX } from '../../../constants/ui';
import { BottomSheet } from '../_components_';

import variables from '../../../_variables.scss';

interface IProps<T> {
  open: boolean;
  onClose: () => void;
  title?: string;
  items?: T[];
  renderItem: (item: T) => React.ReactNode;
  getItemKey: (item: T) => string;
  footerButton?: React.ReactNode;
  focusIndex?: number;
  hideItemsCount?: boolean;
  headerContent?: React.ReactNode;
  bodyFooterContent?: React.ReactNode;
  hideItemDividers?: boolean;
  isFixedHeightOnMobile?: boolean;
}

const buildInsideSubtitle = (itemCount: number) => `What's inside · ${itemCount} item${itemCount === 1 ? '' : 's'}`;

const drawerSx: SxProps<Theme> = {
  zIndex: DRAWER_Z_INDEX,
  '& .MuiDrawer-paper': {
    width: parseInt(variables.sidebarWidth, 10),
    maxWidth: '90vw',
    display: 'flex',
    flexDirection: 'column',
    p: 0,
  },
};

export const ViewDetailsSidebar = <T extends unknown>({
  open,
  onClose,
  title = '',
  items = [],
  renderItem,
  getItemKey,
  footerButton,
  focusIndex,
  hideItemsCount = false,
  headerContent,
  bodyFooterContent,
  hideItemDividers = false,
  isFixedHeightOnMobile = false,
}: IProps<T>) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const itemsBodyRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pendingFocusKeyRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);

  const anchorTo = useCallback((key: string): boolean => {
    const body = itemsBodyRef.current;
    const target = itemRefs.current[key];

    if (!body || !target) {
      return false;
    }
    const bodyRect = body.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    body.scrollTop += targetRect.top - bodyRect.top;

    return true;
  }, []);

  const setItemsBodyRef = useCallback(
    (el: HTMLDivElement | null) => {
      itemsBodyRef.current = el;

      if (el && pendingFocusKeyRef.current && anchorTo(pendingFocusKeyRef.current)) {
        pendingFocusKeyRef.current = null;
      }
    },
    [anchorTo],
  );

  const resolvedSubtitle = !hideItemsCount && items.length > 0 ? buildInsideSubtitle(items.length) : undefined;

  const renderHeader = useMemo(() => {
    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography
            variant={mobile ? 'h6' : 'h5'}
            fontWeight="medium"
            sx={{ ...(!mobile && { minHeight: '1.4em' }), flex: 1, minWidth: 0 }}
          >
            {title}
          </Typography>
          {!mobile && (
            <IconButton onClick={onClose} aria-label="Close drawer" sx={{ p: 0.65 }}>
              <CloseIcon sx={{ width: 20, height: 20 }} />
            </IconButton>
          )}
        </Box>
        {resolvedSubtitle && (
          <Typography color="text.secondary" fontWeight="regular" sx={{ mt: 1, fontSize: 16, lineHeight: 1.4 }}>
            {resolvedSubtitle}
          </Typography>
        )}
      </Box>
    );
  }, [title, resolvedSubtitle, onClose, mobile]);

  const renderItems = useMemo(() => {
    return items.map((item, index) => {
      const isLast = index === items.length - 1;

      return (
        <Box
          key={getItemKey(item)}
          ref={(el: HTMLDivElement | null) => {
            itemRefs.current[getItemKey(item)] = el;
          }}
        >
          {renderItem(item)}
          {!isLast && !hideItemDividers && <Divider sx={{ mt: 1, borderColor: 'grey.400' }} />}
        </Box>
      );
    });
  }, [items, renderItem, getItemKey, hideItemDividers]);

  const footerStyles = useMemo(() => {
    return {
      position: 'sticky',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'grey.100',
      height: mobile ? 'auto' : 84,
      display: 'flex',
      justifyContent: 'end',
      alignItems: 'center',
      p: mobile ? 2 : 3,
      // Consumers pass fullWidth={mobile}; without this the end-justified row shrinks the
      // button back to its label width, so the declared 100% never shows.
      ...(mobile && { '& > *': { flex: 1 } }),
    };
  }, [mobile]);

  const footer = useMemo(() => {
    if (!footerButton) {
      return null;
    }

    return <Box sx={footerStyles}>{footerButton}</Box>;
  }, [footerButton, footerStyles]);

  useLayoutEffect(() => {
    const isJustOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!open || focusIndex === undefined) {
      pendingFocusKeyRef.current = null;
      if (isJustOpened && itemsBodyRef.current) {
        itemsBodyRef.current.scrollTop = 0;
      }

      return;
    }

    const targetItem = items[focusIndex];
    if (!targetItem) {
      return;
    }
    const targetKey = getItemKey(targetItem);

    if (anchorTo(targetKey)) {
      pendingFocusKeyRef.current = null;

      return;
    }
    pendingFocusKeyRef.current = targetKey;

    let frame = 0;
    let rafId: number;

    const retry = () => {
      if (pendingFocusKeyRef.current !== targetKey) {
        return;
      }
      if (anchorTo(targetKey)) {
        pendingFocusKeyRef.current = null;

        return;
      }
      frame += 1;
      if (frame < 15) {
        rafId = requestAnimationFrame(retry);
      }
    };

    rafId = requestAnimationFrame(retry);

    return () => cancelAnimationFrame(rafId);
  }, [open, focusIndex, items, getItemKey, anchorTo]);

  const bottomSheetPaperSx = useMemo(
    () => ({
      px: 0,
      pb: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      // maxHeight alone still collapses to the content of a short list, so the fixed variant
      // pins the height itself and lets the body scroll inside it.
      ...(isFixedHeightOnMobile && { height: `${BOTTOM_SHEET_FIXED_HEIGHT_PERCENT}%` }),
    }),
    [isFixedHeightOnMobile],
  );

  const content = useMemo(() => {
    return (
      <>
        {renderHeader}
        {headerContent}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: hideItemDividers ? 0 : 3, mt: 2 }}>{renderItems}</Box>
        {bodyFooterContent && <Box sx={{ mt: 2 }}>{bodyFooterContent}</Box>}
      </>
    );
  }, [renderHeader, headerContent, renderItems, bodyFooterContent, hideItemDividers]);

  return (
    <>
      {mobile ? (
        <BottomSheet
          open={open}
          onClose={onClose}
          onOpen={() => void 0}
          maxHeightPercent={isFixedHeightOnMobile ? BOTTOM_SHEET_FIXED_HEIGHT_PERCENT : undefined}
          sx={{ '& .MuiDrawer-paper': bottomSheetPaperSx }}
        >
          <Box
            ref={setItemsBodyRef}
            sx={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', minHeight: 0, px: 2 }}
          >
            {content}
          </Box>
          {footer}
        </BottomSheet>
      ) : (
        <Drawer open={open} onClose={onClose} anchor="right" sx={drawerSx}>
          <Box
            ref={setItemsBodyRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              // Keeps the wheel in the drawer: without it, reaching either end chains the scroll
              // onto the page behind.
              overscrollBehavior: 'contain',
              px: 3,
              '& > :first-child': { pt: 3 },
              '& > :last-child': { pb: 3 },
            }}
          >
            {content}
          </Box>
          {footer}
        </Drawer>
      )}
    </>
  );
};
